import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
  Video, Zap, Clock, RefreshCw, Download, Trash2,
  CheckCircle, AlertCircle, Loader, Play, Film
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORMS = [
  { id: 'tiktok',           label: 'TikTok',            aspect: '9:16',  icon: '▶', color: '#fe2c55' },
  { id: 'instagram_reel',   label: 'Instagram Reel',    aspect: '9:16',  icon: '📸', color: '#e4405f' },
  { id: 'instagram_square', label: 'Instagram Portrait', aspect: '9:16', icon: '📸', color: '#c13584' },
  { id: 'facebook',         label: 'Facebook',          aspect: '16:9',  icon: '🌐', color: '#1877f2' },
  { id: 'youtube_short',    label: 'YouTube Short',     aspect: '9:16',  icon: '▶', color: '#ff0000' },
];

const DURATIONS = [
  { value: 4,  label: '4s', badge: 'Fast' },
  { value: 8,  label: '8s', badge: 'Standard' },
  { value: 12, label: '12s', badge: 'Long' },
];

const STYLES = [
  { id: 'dynamic',     label: 'Dynamic',     desc: 'Motion graphics, kinetic energy' },
  { id: 'cinematic',   label: 'Cinematic',   desc: 'Film-grade, dramatic lighting' },
  { id: 'minimal',     label: 'Minimal',     desc: 'Clean, bold, white-space driven' },
  { id: 'neon_tech',   label: 'Neon Tech',   desc: 'Dark bg, glowing accents, cyber' },
  { id: 'professional',label: 'Professional',desc: 'Corporate, trust, brand authority' },
];

function StatusBadge({ status }) {
  const map = {
    queued:     { color: '#7c5efa', bg: 'rgba(124,94,250,0.12)', icon: <Clock size={11} />, label: 'Queued' },
    generating: { color: '#f4c430', bg: 'rgba(244,196,48,0.12)', icon: <Loader size={11} className="animate-spin" />, label: 'Generating...' },
    complete:   { color: '#3dffa0', bg: 'rgba(61,255,160,0.12)', icon: <CheckCircle size={11} />, label: 'Ready' },
    failed:     { color: '#eb5757', bg: 'rgba(235,87,87,0.12)',  icon: <AlertCircle size={11} />, label: 'Failed' },
  };
  const s = map[status] || map.queued;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: s.bg, border: `1px solid ${s.color}44`, color: s.color }}>
      {s.icon}{s.label}
    </span>
  );
}

function VideoCard({ job, onDelete, onRefresh }) {
  const isPolling = ['queued', 'generating'].includes(job.status);
  const platform = PLATFORMS.find(p => p.id === job.platform) || PLATFORMS[0];

  useEffect(() => {
    if (!isPolling) return;
    const t = setInterval(() => onRefresh(job.id), 8000);
    return () => clearInterval(t);
  }, [job.id, isPolling]);

  return (
    <div className="rounded-2xl overflow-hidden" data-testid={`video-card-${job.id}`}
      style={{ background: '#0f0f1a', border: '1px solid rgba(255,255,255,0.07)' }}>
      {/* Thumbnail / Video */}
      <div className="relative flex items-center justify-center"
        style={{
          aspectRatio: job.aspect_ratio === '9:16' ? '9/16' : job.aspect_ratio === '1:1' ? '1/1' : '16/9',
          maxHeight: 260,
          background: 'linear-gradient(135deg, #0d0d20, #1a1a35)',
        }}>
        {job.status === 'complete' && job.video_url ? (
          <video
            src={`${API}${job.video_url}`}
            controls
            autoPlay={false}
            className="w-full h-full object-cover"
            style={{ maxHeight: 260 }}
            data-testid={`video-player-${job.id}`}
          />
        ) : (
          <div className="flex flex-col items-center gap-2" style={{ color: 'rgba(255,255,255,0.2)' }}>
            {job.status === 'generating'
              ? <Loader size={32} className="animate-spin" style={{ color: '#7c5efa' }} />
              : job.status === 'failed'
              ? <AlertCircle size={32} style={{ color: '#eb5757' }} />
              : <Film size={32} />}
            <span className="text-xs">{job.status === 'generating' ? 'Sora 2 is generating...' : job.status === 'failed' ? 'Generation failed' : 'Awaiting generation'}</span>
            {job.status === 'generating' && (
              <span className="text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>Usually 2-5 min</span>
            )}
          </div>
        )}
        {/* Platform tag */}
        <div className="absolute top-2 left-2">
          <span className="text-[10px] font-bold px-2 py-0.5 rounded"
            style={{ background: `${platform.color}22`, color: platform.color, border: `1px solid ${platform.color}44` }}>
            {platform.label} · {job.aspect_ratio}
          </span>
        </div>
        {/* Duration */}
        <div className="absolute top-2 right-2">
          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.6)' }}>
            {job.duration}s
          </span>
        </div>
      </div>

      {/* Info */}
      <div className="p-3">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <div className="font-semibold text-sm text-white leading-tight truncate">{job.title}</div>
          <StatusBadge status={job.status} />
        </div>

        {job.prompt && (
          <p className="text-[11px] leading-relaxed mb-2" style={{ color: 'rgba(255,255,255,0.35)' }}>
            {job.prompt.slice(0, 100)}...
          </p>
        )}
        {job.error && (
          <p className="text-[11px] text-red-400 mb-2">{job.error}</p>
        )}

        <div className="flex items-center gap-1.5">
          {job.status === 'complete' && job.video_url && (
            <a href={`${API}${job.video_url}`} download={`${job.id}.mp4`}
              className="flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-lg"
              style={{ background: 'rgba(61,255,160,0.1)', color: '#3dffa0', border: '1px solid rgba(61,255,160,0.25)' }}>
              <Download size={11} />Download MP4
            </a>
          )}
          <button onClick={() => onRefresh(job.id)} data-testid={`refresh-${job.id}`}
            className="p-1.5 rounded-lg transition-opacity hover:opacity-80"
            style={{ color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <RefreshCw size={11} />
          </button>
          <button onClick={() => onDelete(job.id)} data-testid={`delete-video-${job.id}`}
            className="p-1.5 rounded-lg transition-opacity hover:opacity-80 ml-auto"
            style={{ color: 'rgba(235,87,87,0.5)', border: '1px solid rgba(235,87,87,0.15)' }}>
            <Trash2 size={11} />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SocialVideoCreator() {
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [platform, setPlatform] = useState('tiktok');
  const [duration, setDuration] = useState(8);
  const [style, setStyle] = useState('dynamic');
  const [brand, setBrand] = useState('qlia.io');
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => { loadHistory(); }, []);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/video/history`);
      setJobs(data.jobs || []);
    } catch { }
    finally { setLoading(false); }
  };

  const refreshJob = async (videoId) => {
    try {
      const { data } = await axios.get(`${API}/api/video/status/${videoId}`);
      setJobs(prev => prev.map(j => j.id === videoId ? { ...j, ...data } : j));
    } catch { }
  };

  const deleteJob = async (videoId) => {
    try {
      await axios.delete(`${API}/api/video/job/${videoId}`);
      setJobs(prev => prev.filter(j => j.id !== videoId));
    } catch { }
  };

  const generateVideo = async () => {
    if (!title.trim()) { setError('Title is required'); return; }
    setError('');
    setGenerating(true);
    try {
      const { data } = await axios.post(`${API}/api/video/create`, {
        title: title.trim(),
        content: content.trim() || undefined,
        platform,
        style,
        duration_seconds: duration,
        brand,
      });
      if (data.success) {
        setJobs(prev => [data, ...prev]);
        setTitle('');
        setContent('');
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to start video generation');
    }
    finally { setGenerating(false); }
  };

  const activePlatform = PLATFORMS.find(p => p.id === platform);

  return (
    <div className="h-full flex overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Left: Form panel */}
      <div className="flex flex-col overflow-hidden flex-shrink-0" style={{ width: 340, borderRight: '1px solid var(--llmc-border)' }}>
        {/* Header */}
        <div className="flex items-center gap-2.5 px-4 py-3 flex-shrink-0"
          style={{ background: 'rgba(10,10,20,0.95)', borderBottom: '1px solid var(--llmc-border)' }}>
          <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #fe2c55, #ff8a65)' }}>
            <Video size={16} className="text-white" />
          </div>
          <div>
            <div className="font-black text-white text-sm">Social Video Creator</div>
            <div className="text-[10px]" style={{ color: 'var(--llmc-text-muted)' }}>Sora 2 · AI-generated videos</div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>
              Video Title / Topic *
            </label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g. AI Security Trends 2026"
              data-testid="video-title-input"
              className="w-full px-3 py-2 rounded-xl text-sm text-white"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Content */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>
              Content / Script (optional — Sora will infer from title)
            </label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder="Paste your blog post or key points here..."
              data-testid="video-content-input"
              rows={4}
              className="w-full px-3 py-2 rounded-xl text-sm text-white resize-none"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
            />
          </div>

          {/* Brand */}
          <div>
            <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--llmc-text-muted)' }}>Brand</label>
            <select value={brand} onChange={e => setBrand(e.target.value)} data-testid="video-brand-select"
              className="w-full px-3 py-2 rounded-xl text-sm text-white"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
              <option value="qlia.io">qlia.io</option>
              <option value="iconiqs.ai">iconiqs.ai</option>
              <option value="IdeaWorks">IdeaWorks</option>
            </select>
          </div>

          {/* Platform */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Platform</label>
            <div className="grid grid-cols-1 gap-1.5">
              {PLATFORMS.map(p => (
                <button key={p.id} onClick={() => setPlatform(p.id)} data-testid={`platform-${p.id}`}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-all"
                  style={{
                    background: platform === p.id ? `${p.color}18` : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${platform === p.id ? p.color + '66' : 'rgba(255,255,255,0.06)'}`,
                  }}>
                  <span className="text-xs font-bold w-5" style={{ color: p.color }}>{p.aspect}</span>
                  <span className="text-xs font-medium flex-1" style={{ color: platform === p.id ? '#fff' : 'rgba(255,255,255,0.5)' }}>{p.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Duration</label>
            <div className="flex gap-2">
              {DURATIONS.map(d => (
                <button key={d.value} onClick={() => setDuration(d.value)} data-testid={`duration-${d.value}`}
                  className="flex-1 py-2 rounded-xl text-xs font-bold transition-all"
                  style={{
                    background: duration === d.value ? 'rgba(124,94,250,0.2)' : 'rgba(255,255,255,0.03)',
                    border: `1px solid ${duration === d.value ? 'rgba(124,94,250,0.5)' : 'rgba(255,255,255,0.06)'}`,
                    color: duration === d.value ? '#a68eff' : 'rgba(255,255,255,0.4)',
                  }}>
                  <div>{d.label}</div>
                  <div className="text-[9px] opacity-70">{d.badge}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Style */}
          <div>
            <label className="block text-xs font-semibold mb-2" style={{ color: 'var(--llmc-text-muted)' }}>Visual Style</label>
            <div className="space-y-1">
              {STYLES.map(s => (
                <button key={s.id} onClick={() => setStyle(s.id)} data-testid={`style-${s.id}`}
                  className="w-full flex items-start gap-2 px-3 py-2 rounded-xl text-left transition-all"
                  style={{
                    background: style === s.id ? 'rgba(124,94,250,0.12)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${style === s.id ? 'rgba(124,94,250,0.4)' : 'rgba(255,255,255,0.05)'}`,
                  }}>
                  <div className={`w-3 h-3 mt-0.5 rounded-full border-2 flex-shrink-0 ${style === s.id ? 'border-purple-400 bg-purple-400' : 'border-gray-600'}`} />
                  <div>
                    <div className="text-xs font-semibold" style={{ color: style === s.id ? '#a68eff' : 'rgba(255,255,255,0.6)' }}>{s.label}</div>
                    <div className="text-[10px]" style={{ color: 'rgba(255,255,255,0.2)' }}>{s.desc}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs"
              style={{ background: 'rgba(235,87,87,0.1)', border: '1px solid rgba(235,87,87,0.3)', color: '#eb5757' }}>
              <AlertCircle size={12} />{error}
            </div>
          )}

          <div className="text-[10px] leading-relaxed p-3 rounded-xl"
            style={{ background: 'rgba(254,44,85,0.06)', border: '1px solid rgba(254,44,85,0.15)', color: 'rgba(255,255,255,0.3)' }}>
            Sora 2 generates a real AI video from your content. Generation takes 2-5 minutes. The video will appear in the library when ready.
          </div>
        </div>

        {/* Generate button */}
        <div className="p-4 flex-shrink-0" style={{ borderTop: '1px solid var(--llmc-border)' }}>
          <button onClick={generateVideo} disabled={generating || !title.trim()} data-testid="generate-video-btn"
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl font-black text-sm text-white transition-all"
            style={{
              background: generating ? 'rgba(124,94,250,0.3)' : 'linear-gradient(135deg, #fe2c55, #c4143f)',
              opacity: !title.trim() ? 0.4 : 1,
            }}>
            {generating
              ? <><Loader size={16} className="animate-spin" />Generating with Sora 2...</>
              : <><Zap size={16} />Generate Video</>}
          </button>
        </div>
      </div>

      {/* Right: Video gallery */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Gallery header */}
        <div className="flex items-center gap-3 px-5 py-3 flex-shrink-0"
          style={{ background: 'rgba(10,10,20,0.95)', borderBottom: '1px solid var(--llmc-border)' }}>
          <Film size={18} style={{ color: '#7c5efa' }} />
          <span className="font-bold text-sm text-white">Video Library</span>
          <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
            style={{ background: 'rgba(124,94,250,0.12)', color: '#a68eff', border: '1px solid rgba(124,94,250,0.25)' }}>
            {jobs.length} videos
          </span>
          <button onClick={loadHistory} className="ml-auto p-1.5 rounded-lg hover:opacity-70" style={{ color: 'rgba(255,255,255,0.3)' }}>
            <RefreshCw size={14} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center h-40">
              <Loader size={24} className="animate-spin" style={{ color: '#7c5efa' }} />
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
                style={{ background: 'rgba(254,44,85,0.08)', border: '1px solid rgba(254,44,85,0.15)' }}>
                <Play size={28} style={{ color: 'rgba(254,44,85,0.4)' }} />
              </div>
              <div className="text-sm font-semibold text-white mb-1">No videos yet</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                Fill in a title and hit Generate to create your first Sora 2 video
              </div>
            </div>
          ) : (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
              {jobs.map(job => (
                <VideoCard key={job.id} job={job} onDelete={deleteJob} onRefresh={refreshJob} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
